/** Where each vendor is priced: in country (UAE), the cheapest region
 *  anywhere ("outside"), or one named region -- one choice per vendor, with a
 *  switch that sets them all at once. The choice is remembered in the browser
 *  and shared by the Calculator, Compare and Fit pages, so a quote made on one
 *  page is for the same regions as the next. */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../api'
import { ANY_REGION, IN_COUNTRY, choiceCountry, countryChoice, type Provider, type RegionChoices } from '../types'
import { useSettings, useSite, VENDOR_LABEL } from '../settings'
import { countryName, useCountry } from './geoDetect'
import { Help } from './ui'
import { useAsync } from './useAsync'

const KEY = 'regions'
const ALL: Provider[] = ['alibaba', 'anthropic', 'aws', 'azure', 'cloudflare', 'core42', 'crusoe', 'databricks', 'deepinfra', 'fireworks', 'gemini', 'gcp', 'groq', 'hfendpoints', 'huawei', 'watsonx', 'mistral', 'nebius', 'oci', 'openai', 'snowflake', 'telnyx', 'together', 'xai']
const fresh = (): RegionChoices => ({ aws: ANY_REGION, azure: ANY_REGION, oci: ANY_REGION, core42: ANY_REGION, alibaba: ANY_REGION, gcp: ANY_REGION, nebius: ANY_REGION, mistral: ANY_REGION, telnyx: ANY_REGION, snowflake: ANY_REGION, cloudflare: ANY_REGION, databricks: ANY_REGION, gemini: ANY_REGION, hfendpoints: ANY_REGION, watsonx: ANY_REGION, xai: ANY_REGION, together: ANY_REGION, crusoe: ANY_REGION, huawei: ANY_REGION, groq: ANY_REGION, fireworks: ANY_REGION, deepinfra: ANY_REGION, openai: ANY_REGION, anthropic: ANY_REGION })

function load(): RegionChoices {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) {
      const got = JSON.parse(raw) as Partial<RegionChoices>
      const out = fresh()
      // a vendor added since the choice was saved takes the choice the saved ones all share ("in country"
      // stays in country for it too), and the cheapest region anywhere when they differ
      const saved = ALL.map((p) => got[p]).filter((v): v is string => typeof v === 'string' && !!v)
      const shared = saved.length && saved.every((v) => choiceCountry(v) !== null && choiceCountry(v) === choiceCountry(saved[0]))
        ? saved[0] : undefined
      for (const p of ALL) out[p] = typeof got[p] === 'string' && got[p] ? got[p] as string : shared ?? ANY_REGION
      return out
    }
  } catch { /* storage blocked or garbage: start fresh */ }
  return fresh()
}

type Mode = 'in-country' | 'outside' | 'custom'
export const modeOf = (r: RegionChoices, vendors: Provider[]): Mode => {
  const vals = vendors.map((p) => r[p])
  const where = vals.map((v) => choiceCountry(v))
  if (where.every((c) => c !== null) && new Set(where).size === 1) return 'in-country'
  if (vals.every((v) => v === ANY_REGION)) return 'outside'
  return 'custom'
}

/** The shared region choice. Every page reads the same stored value; a change
 *  on one page reaches the others through the storage event. */
export function useRegions(): [RegionChoices, (next: RegionChoices) => void] {
  const [regions, setState] = useState<RegionChoices>(load)
  useEffect(() => {
    const onStorage = (e: StorageEvent) => { if (e.key === KEY) setState(load()) }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])
  const set = useCallback((next: RegionChoices) => {
    setState(next)
    try { localStorage.setItem(KEY, JSON.stringify(next)) } catch { /* storage blocked */ }
  }, [])
  return [regions, set]
}

/** Only the vendors that are switched on, for the request body: a vendor the
 *  administrator switched off is not priced, so its choice is irrelevant. */
export function regionsFor(regions: RegionChoices, vendors: Provider[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const p of vendors) out[p] = regions[p]
  return out
}

/** A short label for a vendor's choice, for tags next to prices. */
export function regionLabel(choice: string | undefined, country = 'UAE'): string {
  if (!choice || choice === ANY_REGION) return 'cheapest region'
  if (choice === IN_COUNTRY) return `in country (${country})`
  const code = choiceCountry(choice)
  if (code) return `in country (${countryName(code)})`
  return choice
}

export function RegionPicker({ regions, onChange, compact = false, bare = false, split = false }: {
  regions: RegionChoices; onChange: (r: RegionChoices) => void; compact?: boolean
  /** no box and no label: the picker sits under its own heading */
  bare?: boolean
  /** no box; the switch and the vendor row become items of the parent grid,
   *  so the switch can share a row with another control and the vendors
   *  take a full row beneath */
  split?: boolean
}) {
  const { vendors } = useSettings()
  // every region any vendor prices anything in, plus the in-country mapping
  const meta = useAsync(() => api.regionsMeta(), [])
  const options = useMemo(() => {
    const out: Record<Provider, string[]> = { aws: [], azure: [], oci: [], core42: [], alibaba: [], gcp: [], nebius: [], mistral: [], telnyx: [], snowflake: [], cloudflare: [], databricks: [], gemini: [], hfendpoints: [], watsonx: [], xai: [], together: [], crusoe: [], huawei: [], groq: [], fireworks: [], deepinfra: [], openai: [], anthropic: [] }
    const got = meta.data?.results ?? {}
    for (const p of ALL) out[p] = (got[p] ?? []).filter((r) => r !== 'global').sort()
    return out
  }, [meta.data])
  const inCountry = meta.data?.in_country?.regions ?? {}
  const homeName = meta.data?.in_country?.country ?? 'UAE'
  const home = meta.data?.home_country ?? 'AE'
  const mode = modeOf(regions, vendors)
  // Opex finds where the visitor is and prices "in country" for that country;
  // the other site keeps the UAE it always had
  const live = useSite().features.locate
  const here = useCountry()
  const countries = meta.data?.countries ?? {}
  const known = (c: string | null) => (c && countries[c] ? c : null)
  const selected = live ? (known(here.code) ?? home) : home
  const country = selected === home ? homeName : (countries[selected]?.name ?? selected)
  const mine = (p: Provider) => (countries[selected]?.regions?.[p] ?? inCountry[p] ?? [])
  // "Per vendor" opens the per-vendor dropdowns on the same line; they stay
  // open while any vendor differs from the others, or until In country /
  // Outside is pressed
  const [open, setOpen] = useState(mode === 'custom')
  useEffect(() => { if (mode === 'custom') setOpen(true) }, [mode])
  // In country and Outside set every vendor and close the dropdowns; only
  // Per vendor opens them
  const setAll = (v: string) => { setOpen(false); onChange({ ...regions, ...Object.fromEntries(vendors.map((p) => [p, v])) } as RegionChoices) }
  const setOne = (p: Provider, v: string) => onChange({ ...regions, [p]: v })
  const showVendors = open || mode === 'custom'
  // "In country" means the country shown: when it is on and the country
  // changes (found, or chosen on another page), every vendor follows
  const wanted = countryChoice(selected, home)
  useEffect(() => {
    if (!live || !meta.data || mode !== 'in-country') return
    if (vendors.some((p) => regions[p] !== wanted)) setAll(wanted)
  }, [live, meta.data, mode, wanted]) // eslint-disable-line react-hooks/exhaustive-deps
  const pickCountry = (code: string) => { here.choose(code); setAll(countryChoice(code, home)) }
  const unsupported = here.code && !known(here.code) ? countryName(here.code) : null

  return (
    <div className={`regionpick${compact ? ' compact' : ''}${bare ? ' bare' : ''}${split ? ' split' : ''}`}>
      <div className="modes" role="group" aria-label="Where the vendors are priced">
        {bare ? null : <span className="lbl">Where it runs<Help text={`In country prices every vendor only in its ${country} region (AWS me-central-1, Azure uaenorth, OCI Dubai/Abu Dhabi per Oracle's docs, Core42 uae); a vendor that does not sell the model there is shown as not sold, never priced from another region. Cheapest region takes each vendor's cheapest region. Per vendor lets you pick a region for each. Input and output prices always come from the same region and tier.`} /></span>}
        {live ? (
          <span className={`locbadge ${here.phase === 'detecting' ? 'busy' : here.code && known(here.code) ? 'ok' : ''}`} role="status" aria-live="polite"
            title="Worked out in your browser from your time zone. Nothing is sent anywhere.">
            <span className="sig" aria-hidden="true"><i /><b /><b /></span>
            <span className="txt">
              {here.phase === 'detecting' ? 'Detecting your location…'
                : unsupported ? `${unsupported} detected · no cloud region there`
                : here.code ? `${country} ${here.chosen ? '· your choice' : 'detected'}`
                : 'Location unknown · choose a country'}
            </span>
            <select className="locpick" value="" aria-label="Choose another country" onChange={(e) => e.target.value && pickCountry(e.target.value)}>
              <option value="">Change</option>
              {Object.entries(countries).map(([code, c]) => <option key={code} value={code}>{c.name}</option>)}
            </select>
            <button type="button" className="locagain" aria-label="Detect my location again" title="Detect again" onClick={here.detectAgain}>↻</button>
          </span>
        ) : null}
        <span className="seg">
          <button type="button" aria-pressed={!showVendors && mode === 'in-country'} onClick={() => setAll(wanted)}>In country</button>
          <button type="button" aria-pressed={!showVendors && mode === 'outside'} onClick={() => setAll(ANY_REGION)}>Cheapest region</button>
          <button type="button" aria-pressed={showVendors} onClick={() => setOpen(true)}>Per vendor</button>
        </span>
      </div>
      {showVendors ? <div className="vendors inline">
        {vendors.map((p) => {
          const homeRegions = mine(p).join(' / ')
          const choice = regions[p]
          const listed = options[p].includes(choice) || choice === wanted
          return (
            <label key={p} className={`v ${p}`}>
              <span className="name">{VENDOR_LABEL[p]}</span>
              <select value={choice} onChange={(e) => setOne(p, e.target.value)}>
                <option value={wanted}>In country · {country}{homeRegions ? ` · ${homeRegions}` : ''}</option>
                <option value={ANY_REGION}>Cheapest region</option>
                {p === 'oci' ? <option value="" disabled>— price is global; regions per Oracle's docs —</option> : null}
                {options[p].map((r) => <option key={r} value={r}>{r}</option>)}
                {!listed && choice !== ANY_REGION ? <option value={choice}>{regionLabel(choice)}</option> : null}
              </select>
            </label>
          )
        })}
        </div> : null}
    </div>
  )
}
